// Object-driven sidebar panels for the Workspace app.
//
// Pure JSON interpreted by the engine in ./engine — no bespoke React, and none
// should be added: if a look or behaviour can't be expressed, extend the
// engine's vocabulary once (schema + engine) and select it from JSON.
//
// Panel `id` must match the `panelId` on the matching rail item in ../apps.ts.

import {
  SIDEBAR_SCHEMA_VERSION,
  type PanelConfig,
  type RowConfig,
  type SidebarConfig,
} from '@clarix/shared-types';

/* ────────────────────────── Settings ────────────────────────── */

/** Mirrors SECTIONS in apps/workspace/src/pages/workspace/OrganizationSettings.tsx. */
const configSection = (id: string, label: string, icon: string): RowConfig => ({
  id,
  label,
  icon,
  toParam: { pathname: '/workspace/configuration', param: 'section', value: id },
});

/**
 * The unified workspace Settings panel: organization config sub-sections, then
 * access, security, and the workspace master lists. Groups are separated by
 * whitespace (`spaced`) rather than rules, matching the Projects Settings panel
 * so both apps read the same way.
 *
 * Each group is gated on the admin capability its pages need, published by the
 * remote's lib/shell-permissions.ts. Owners hold them all; a Member granted one
 * capability sees only that group.
 */
const settingsPanel: PanelConfig = {
  id: 'workspace-settings',
  title: 'Settings',
  // Neither /configuration nor /masters carries its param on first open — each
  // group's first visible row is what the page actually shows.
  defaultActiveFirst: true,
  sections: [
    {
      id: 'general',
      label: 'General',
      visibleWhen: { permission: 'manage_settings' },
      rows: [
        configSection('company', 'Company Profile', 'Building2'),
        configSection('regional', 'Regional Settings', 'Globe'),
        configSection('billing', 'Billing Defaults', 'CreditCard'),
        configSection('work', 'Work Settings', 'Clock'),
      ],
    },
    {
      // Admin-console roles & permissions — no longer a top-level rail icon,
      // managed here in Settings. Editing templates is OWNER-only server-side;
      // this gates who can SEE the roles at all.
      id: 'access',
      label: 'Access',
      spaced: true,
      visibleWhen: { permission: 'manage_members' },
      rows: [
        {
          id: 'permissions',
          label: 'Roles & permissions',
          icon: 'ShieldCheck',
          to: '/workspace/permissions',
        },
        {
          id: 'joiner-access',
          label: 'New joiner access',
          icon: 'UserPlus',
          to: '/workspace/joiner-access',
          visibleWhen: { permission: 'manage_invites' },
        },
      ],
    },
    {
      // Platform-wide password policy + IP allowlist, overridable per-workspace.
      id: 'security',
      label: 'Security',
      spaced: true,
      visibleWhen: { permission: 'manage_security' },
      rows: [
        {
          id: 'two-factor',
          label: 'Two-factor authentication',
          icon: 'KeyRound',
          to: '/workspace/security',
          // Exact: /workspace/security IS the 2FA page, and the two rows below
          // are deeper paths that must not light this one up as well.
          exact: true,
        },
        {
          id: 'password-policy',
          label: 'Password policy',
          icon: 'Lock',
          to: '/workspace/security/password-policy',
        },
        {
          id: 'ip-allowlist',
          label: 'IP allowlist',
          icon: 'Globe',
          to: '/workspace/security/ip-allowlist',
        },
      ],
    },
    {
      id: 'master',
      label: 'Master',
      spaced: true,
      visibleWhen: { permission: 'manage_masters' },
      loadingVariant: 'text',
      emptyText: 'No lists',
      rowsSource: { provider: 'workspaceMasters' },
    },
  ],
};

export const workspaceSidebar: SidebarConfig = {
  version: SIDEBAR_SCHEMA_VERSION,
  panels: [settingsPanel],
};
